(() => {
  const CACHE_PREFIX = 'td-community-yotpo:';

  const formatScore = (value) => (Math.round(value * 10) / 10).toFixed(1);

  const formatCount = (total) => `${total.toLocaleString('en-US')}+ reviews`;

  const getPercentages = (distribution) => {
    const total = [1, 2, 3, 4, 5].reduce((sum, star) => sum + (distribution[String(star)] || 0), 0) || 1;
    const percentages = {};

    for (let star = 1; star <= 5; star += 1) {
      percentages[star] = Math.round(((distribution[String(star)] || 0) / total) * 1000) / 10;
    }

    return percentages;
  };

  const readCache = (cacheKey) => {
    try {
      const raw = localStorage.getItem(`${CACHE_PREFIX}${cacheKey}`);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      if (!parsed?.fetchedAt || !parsed?.distribution) return null;

      return parsed;
    } catch {
      return null;
    }
  };

  const writeCache = (cacheKey, payload) => {
    try {
      localStorage.setItem(`${CACHE_PREFIX}${cacheKey}`, JSON.stringify(payload));
    } catch {
      /* ignore quota errors */
    }
  };

  const resolveProductId = async (productId, productHandle) => {
    if (productId) return productId;

    if (!productHandle) return null;

    const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${productHandle}.json`);
    if (!response.ok) return null;

    const data = await response.json();
    return data?.product?.id ? String(data.product.id) : null;
  };

  const fetchYotpoData = async (appKey, productId) => {
    const url = `https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/products/${encodeURIComponent(productId)}/reviews.json?per_page=1`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Yotpo request failed (${response.status})`);

    const data = await response.json();
    const bottomline = data?.response?.bottomline;
    if (!bottomline?.star_distribution) throw new Error('Yotpo response missing star distribution');

    const averageScore = bottomline.average_score ?? bottomline.averageScore ?? 0;
    const totalReviews = bottomline.total_review ?? bottomline.total_reviews ?? 0;
    const distribution = bottomline.star_distribution;

    return {
      fetchedAt: Date.now(),
      averageScore,
      totalReviews,
      distribution,
      percentages: getPercentages(distribution),
    };
  };

  const applyData = (root, data) => {
    const scoreEl = root.querySelector('[data-td-community-score]');
    const countEl = root.querySelector('[data-td-community-count]');
    const barsEl = root.querySelector('[data-td-community-bars]');

    if (scoreEl) scoreEl.textContent = formatScore(data.averageScore);
    if (countEl) countEl.textContent = formatCount(data.totalReviews);

    root.querySelectorAll('[data-td-community-bar]').forEach((bar) => {
      const star = Number(bar.dataset.tdCommunityBar);
      const width = data.percentages[star] ?? 0;
      bar.style.width = `${width}%`;
    });

    if (barsEl) {
      barsEl.setAttribute(
        'aria-label',
        `${formatScore(data.averageScore)} out of 5 stars based on ${data.totalReviews} reviews`
      );
    }

    root.dataset.tdCommunityYotpoLoaded = 'true';
  };

  const initRoot = async (root) => {
    const appKey = root.dataset.yotpoAppKey;
    const productId = root.dataset.yotpoProductId || '';
    const productHandle = root.dataset.yotpoProductHandle || '';
    const cacheHours = Number(root.dataset.yotpoCacheHours || 24);
    const cacheKey = root.dataset.yotpoCacheKey || `${appKey}:${productId || productHandle}`;
    const cacheTtlMs = Math.max(1, cacheHours) * 60 * 60 * 1000;

    if (!appKey || (!productId && !productHandle)) return;

    const cached = readCache(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < cacheTtlMs) {
      applyData(root, cached);
      return;
    }

    try {
      const resolvedProductId = await resolveProductId(productId, productHandle);
      if (!resolvedProductId) return;

      const data = await fetchYotpoData(appKey, resolvedProductId);
      writeCache(cacheKey, data);
      applyData(root, data);
    } catch {
      if (cached) applyData(root, cached);
    }
  };

  const init = () => {
    document.querySelectorAll('[data-td-community-yotpo]').forEach((root) => {
      initRoot(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
